import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { ManifestBlockField } from '../../tarifs/ui/PublishWizard';
import { useCoreConfigs } from '../api/authoring';
import { joinTarifRefs, splitTarifRefs } from '../model/tarif-refs';

/**
 * A single tarif's `refs` block: two pickers over this tarif's own published assets, and a text
 * field for everything else.
 *
 * The console already has these assets — it drew the asset lists from them one screen ago — so
 * asking for a typed ERN it could look up is a transcription error waiting to happen, and one the
 * gate would refuse with a 409 several screens later. Only PUBLISHED assets are offered: a draft
 * is not pinnable, and the gate says so.
 *
 * `tarifDeckung` is optional and stays optional. The manifest schema requires `insuranceCore` and
 * nothing else, and prompt 14.3's guard checks exactly that — a tarif does not wait for its
 * coverage document, because a version can be published without one. A picker that made it
 * required would be a stricter rule than the platform has.
 *
 * # Why each picker still has a way out
 *
 * A picker that can only offer publishable pins is right about the common case and wrong as an
 * absolute: an operator legitimately needs to name a ref the console does not know about — an
 * asset published in another window, or one they are deliberately testing the gate against. The
 * gate is what refuses a bad pin, and it does so with a report an actuary can read; a console that
 * made the bad pin unexpressible would not have prevented the mistake, it would have prevented the
 * CHECK.
 *
 * So the last option in each list opens a plain ERN field. The picker stays the primary control,
 * and the escape stays reachable.
 */
const CUSTOM = '__custom__';
export function TarifRefsField({
  tarifSlug,
  value,
  onChange,
  error,
}: {
  readonly tarifSlug: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const cores = useCoreConfigs(tarifSlug, { assetType: 'insurance_core' });
  const deckungen = useCoreConfigs(tarifSlug, { assetType: 'tarif_deckung' });
  const lifecycles = useCoreConfigs(tarifSlug, { assetType: 'lead_lifecycle' });
  const billingConfigs = useCoreConfigs(tarifSlug, { assetType: 'billing_config' });

  const refs = splitTarifRefs(value);
  // Sticky once chosen: without it, clearing the field would snap back to the dropdown and take
  // the half-typed ERN with it.
  const [customCore, setCustomCore] = useState(false);
  const [customDeckung, setCustomDeckung] = useState(false);
  const [customLifecycle, setCustomLifecycle] = useState(false);
  const [customBilling, setCustomBilling] = useState(false);

  const published = (query: typeof cores): ConfigApi.CoreConfig[] =>
    (query.data?.coreConfigs ?? []).filter((asset) => asset.status === 'ASSET_STATUS_PUBLISHED');

  const coreAssets = published(cores);
  const deckungAssets = published(deckungen);
  const lifecycleAssets = published(lifecycles);
  const billingAssets = published(billingConfigs);

  const update = (patch: Partial<typeof refs>): void => {
    onChange(joinTarifRefs({ ...refs, ...patch }));
  };

  // A pin the picker cannot explain — one typed by hand, or an asset superseded while the wizard
  // was open — puts the field into its escape rather than silently rendering as "nothing chosen".
  const coreIsCustom =
    customCore ||
    (refs.insuranceCore !== '' && !coreAssets.some((a) => a.ern === refs.insuranceCore));
  const deckungIsCustom =
    customDeckung ||
    (refs.tarifDeckung !== '' && !deckungAssets.some((a) => a.ern === refs.tarifDeckung));
  const lifecycleIsCustom =
    customLifecycle ||
    (refs.leadLifecycle !== '' && !lifecycleAssets.some((a) => a.ern === refs.leadLifecycle));
  const billingIsCustom =
    customBilling ||
    (refs.billingConfig !== '' && !billingAssets.some((a) => a.ern === refs.billingConfig));

  return (
    <Stack spacing={2}>
      <TextField
        select
        required
        label={t('authoring.version.insuranceCore')}
        value={coreIsCustom ? CUSTOM : refs.insuranceCore}
        onChange={(event) => {
          if (event.target.value === CUSTOM) {
            setCustomCore(true);
            return;
          }
          setCustomCore(false);
          update({ insuranceCore: event.target.value });
        }}
        fullWidth
        disabled={cores.isPending}
        error={error !== '' && !coreIsCustom}
        helperText={error === '' || coreIsCustom ? t('authoring.version.insuranceCoreHint') : error}
      >
        {coreAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM}>{t('authoring.version.customErn')}</MenuItem>
      </TextField>

      {coreIsCustom && (
        <TextField
          label={t('authoring.version.insuranceCoreErn')}
          value={refs.insuranceCore}
          onChange={(event) => {
            update({ insuranceCore: event.target.value });
          }}
          fullWidth
          error={error !== ''}
          helperText={error === '' ? t('authoring.version.customErnHint') : error}
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
        />
      )}

      <TextField
        select
        label={t('authoring.version.tarifDeckung')}
        value={deckungIsCustom ? CUSTOM : refs.tarifDeckung}
        onChange={(event) => {
          if (event.target.value === CUSTOM) {
            setCustomDeckung(true);
            return;
          }
          setCustomDeckung(false);
          update({ tarifDeckung: event.target.value });
        }}
        fullWidth
        disabled={deckungen.isPending}
        helperText={t('authoring.version.tarifDeckungHint')}
      >
        <MenuItem value="">{t('authoring.version.noDeckung')}</MenuItem>
        {deckungAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM}>{t('authoring.version.customErn')}</MenuItem>
      </TextField>

      {deckungIsCustom && (
        <TextField
          label={t('authoring.version.tarifDeckungErn')}
          value={refs.tarifDeckung}
          onChange={(event) => {
            update({ tarifDeckung: event.target.value });
          }}
          fullWidth
          helperText={t('authoring.version.customErnHint')}
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
        />
      )}

      {/* The lead lifecycle (SDD-013 §6.1): optional, and "none" means the platform default
          machine rather than no machine — which is what the empty option says. */}
      <TextField
        select
        label={t('authoring.version.leadLifecycle')}
        value={lifecycleIsCustom ? CUSTOM : refs.leadLifecycle}
        onChange={(event) => {
          if (event.target.value === CUSTOM) {
            setCustomLifecycle(true);
            return;
          }
          setCustomLifecycle(false);
          update({ leadLifecycle: event.target.value });
        }}
        fullWidth
        disabled={lifecycles.isPending}
        helperText={t('authoring.version.leadLifecycleHint')}
      >
        <MenuItem value="">{t('authoring.version.platformDefaultLifecycle')}</MenuItem>
        {lifecycleAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM}>{t('authoring.version.customErn')}</MenuItem>
      </TextField>

      {lifecycleIsCustom && (
        <TextField
          label={t('authoring.version.leadLifecycleErn')}
          value={refs.leadLifecycle}
          onChange={(event) => {
            update({ leadLifecycle: event.target.value });
          }}
          fullWidth
          helperText={t('authoring.version.customErnHint')}
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
        />
      )}

      {/* The billing rules (SDD-015 §5.5, B-7): optional, and "none" means the platform defaults
          rather than no rules — which is what the empty option says. */}
      <TextField
        select
        label={t('authoring.version.billingConfig')}
        value={billingIsCustom ? CUSTOM : refs.billingConfig}
        onChange={(event) => {
          if (event.target.value === CUSTOM) {
            setCustomBilling(true);
            return;
          }
          setCustomBilling(false);
          update({ billingConfig: event.target.value });
        }}
        fullWidth
        disabled={billingConfigs.isPending}
        helperText={t('authoring.version.billingConfigHint')}
      >
        <MenuItem value="">{t('authoring.version.platformDefaultBilling')}</MenuItem>
        {billingAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM}>{t('authoring.version.customErn')}</MenuItem>
      </TextField>

      {billingIsCustom && (
        <TextField
          label={t('authoring.version.billingConfigErn')}
          value={refs.billingConfig}
          onChange={(event) => {
            update({ billingConfig: event.target.value });
          }}
          fullWidth
          helperText={t('authoring.version.customErnHint')}
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
        />
      )}

      {/* The schema allows further refs beside these; nothing here can offer candidates for refs
          another service owns, so they stay free text. */}
      <ManifestBlockField
        block="refs"
        value={refs.other}
        onChange={(next) => {
          update({ other: next });
        }}
        error=""
      />
    </Stack>
  );
}
